import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from '../../i18n';
import '../styles/TrainerLibraryManager.css';
import API_BASE_URL from '../../config/api';

const API = `${API_BASE_URL.replace(/\/+$/, '')}/api`;
const FILTERS_KEY = 'trainerLibrary.filters';

export default function TrainerLibraryManager() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ---- Filtros / paginación (persisten en localStorage)
  const [scope, setScope] = useState('me'); // me | org | all (placeholder)
  const [sport, setSport] = useState('correr');
  const [tag, setTag] = useState('base');   // base | threshold | vo2
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(0);

  // ---- Datos
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // ---- Creación / edición
  const [editingId, setEditingId] = useState(null); // null => creando
  const [newTpl, setNewTpl] = useState({
    name: '',
    sport: 'correr',
    tags: 'base',
    intensity: 'base',
    description: '',
    expected: { durationMin: 0, distanceKm: 0, tss: 0 },
    active: true
  });

  // Refs para UX
  const searchRef = useRef(null);
  const nameRef = useRef(null);
  const creatorCardRef = useRef(null);

  const skip = useMemo(() => page * limit, [page, limit]);

  // ---- Cargar filtros guardados
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
      if (saved.sport) setSport(saved.sport);
      if (saved.tag) setTag(saved.tag);
      if (typeof saved.limit === 'number') setLimit(saved.limit);
      if (typeof saved.q === 'string') setQ(saved.q);
      if (typeof saved.scope === 'string') setScope(saved.scope);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Guardar filtros
  useEffect(() => {
    localStorage.setItem(
      FILTERS_KEY,
      JSON.stringify({ scope, sport, tag, q, limit })
    );
  }, [scope, sport, tag, q, limit]);

  async function fetchData() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/training-library`, {
        params: { sport, tag, search: q || '', limit, skip }
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('❌ Error listando librería:', e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport, tag, limit, skip]);

  // ---- Atajos: Esc => volver, "/" => foco búsqueda, Ctrl/Cmd+N => foco nombre
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        navigate('/trainer-dashboard');
      } else if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if ((e.key === 'n' || e.key === 'N') && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        nameRef.current?.focus();
        creatorCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // ---- Crear / Actualizar
  async function saveTemplate() {
    const isEdit = !!editingId;
    if (!newTpl.name.trim()) return alert(t('trainerLibraryManager.templateNameRequired'));

    const payload = {
      name: newTpl.name.trim(),
      sport: newTpl.sport,
      tags: newTpl.tags,
      intensity: newTpl.intensity,
      description: newTpl.description.trim(),
      expected: {
        durationMin: Number(newTpl.expected.durationMin || 0),
        distanceKm: Number(newTpl.expected.distanceKm || 0),
        tss: Number(newTpl.expected.tss || 0)
      },
      active: true
    };

    try {
      if (isEdit) {
        await axios.put(`${API}/training-library/${editingId}`, payload);
      } else {
        await axios.post(`${API}/training-library`, payload);
      }
      resetCreator();
      fetchData();
      window.dispatchEvent(new Event('library:changed'));
      alert(isEdit ? t('trainerLibraryManager.templateUpdated') : t('trainerLibraryManager.templateCreated'));
    } catch (e) {
      console.error('❌ Error al guardar:', e);
      alert(t('trainerLibraryManager.templateSaveError'));
    }
  }

  function resetCreator() {
    setEditingId(null);
    setNewTpl({
      name: '',
      sport,
      tags: tag,
      intensity: 'base',
      description: '',
      expected: { durationMin: 0, distanceKm: 0, tss: 0 },
      active: true
    });
  }

  // ---- Acciones sobre items
  function startEdit(doc) {
    setEditingId(doc._id);
    setNewTpl({
      name: doc.name || '',
      sport: doc.sport || 'correr',
      tags: doc.tags || 'base',
      intensity: doc.intensity || 'base',
      description: doc.description || '',
      expected: {
        durationMin: doc?.expected?.durationMin || 0,
        distanceKm: doc?.expected?.distanceKm || 0,
        tss: doc?.expected?.tss || 0
      },
      active: !!doc.active
    });
    creatorCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => nameRef.current?.focus(), 50);
  }

  function duplicate(doc) {
    setEditingId(null);
    setNewTpl({
      name: `${doc.name || t('trainerLibraryManager.template')} (${t('trainerLibraryManager.copy')})`,
      sport: doc.sport || 'correr',
      tags: doc.tags || 'base',
      intensity: doc.intensity || 'base',
      description: doc.description || '',
      expected: {
        durationMin: doc?.expected?.durationMin || 0,
        distanceKm: doc?.expected?.distanceKm || 0,
        tss: doc?.expected?.tss || 0
      },
      active: true
    });
    creatorCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => nameRef.current?.focus(), 50);
  }

  async function softDelete(id) {
    if (!window.confirm(t('trainerLibraryManager.confirmDisableTemplate'))) return;
    try {
      await axios.delete(`${API}/training-library/${id}`);
      if (editingId === id) resetCreator();
      fetchData();
      window.dispatchEvent(new Event('library:changed'));
      alert(t('trainerLibraryManager.templateDisabled'));
    } catch (e) {
      console.error('❌ No se pudo desactivar:', e);
      alert(t('trainerLibraryManager.templateDisableError'));
    }
  }

  function totalPages() {
    return Math.max(1, Math.ceil(total / limit));
  }

  return (
    <div className="tlm tlm-page">
      {/* Toolbar sticky: sólo vuelta al dashboard */}
      <div className="tlm-toolbar">
        <div className="container d-flex align-items-center justify-content-between py-2">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-sm btn-light"
              onClick={() => navigate('/trainer-dashboard')}
              title={t('trainerLibraryManager.backToDashboardTitle')}
            >
              ← {t('trainerLibraryManager.back')}
            </button>
            <h4 className="m-0 text-white">{t('trainerLibraryManager.title')}</h4>
          </div>
          <div />
        </div>
      </div>

      <div className="container py-4 tlm-container">
        {/* Grid 2 columnas: 360px fija + 1fr */}
        <div className="tlm-two-col">
          {/* Columna izquierda: filtros + creador/edición */}
          <aside className="tlm-left">
            {/* Filtros */}
            <div className="card shadow-sm tlm-card">
              <div className="card-body">
                <div className="tlm-card-title">{t('trainerLibraryManager.filters')}</div>

                <div className="mb-2">
                  <label className="form-label mb-1">{t('trainerLibraryManager.scope')}</label>
                  <select
                    className="form-select form-select-sm"
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                  >
                    <option value="me">{t('trainerLibraryManager.scopeMine')}</option>
                    <option value="org">{t('trainerLibraryManager.scopeClub')}</option>
                    <option value="all">{t('trainerLibraryManager.scopeAll')}</option>
                  </select>
                </div>

                <div className="row">
                  <div className="col-6 mb-2">
                    <label className="form-label mb-1">{t('trainerLibraryManager.sport')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={sport}
                      onChange={(e) => { setSport(e.target.value); setPage(0); }}
                    >
                      <option value="correr">{t('activityLabels.running')}</option>
                      <option value="ciclismo">{t('activityLabels.cycling')}</option>
                      <option value="nadar">{t('activityLabels.swimming')}</option>
                      <option value="fuerza">{t('activityLabels.strength')}</option>
                    </select>
                  </div>
                  <div className="col-6 mb-2">
                    <label className="form-label mb-1">{t('trainerLibraryManager.tag')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={tag}
                      onChange={(e) => { setTag(e.target.value); setPage(0); }}
                    >
                      <option value="base">{t('trainerLibraryManager.tagBase')}</option>
                      <option value="threshold">{t('trainerLibraryManager.tagThreshold')}</option>
                      <option value="vo2">{t('trainerLibraryManager.tagVo2')}</option>
                    </select>
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label mb-1">{t('trainerLibraryManager.search')}</label>
                  <input
                    ref={searchRef}
                    className="form-control form-control-sm"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (setPage(0), fetchData())}
                    placeholder={t('trainerLibraryManager.searchPlaceholder')}
                  />
                </div>

                <div className="d-flex align-items-end justify-content-between">
                  <div style={{ width: 100 }}>
                    <label className="form-label mb-1">{t('trainerLibraryManager.limit')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={limit}
                      onChange={(e) => { setLimit(Number(e.target.value)); setPage(0); }}
                    >
                      <option>10</option>
                      <option>20</option>
                      <option>50</option>
                      <option>100</option>
                    </select>
                  </div>
                  <button
                    className="btn btn-primary btn-sm mt-3"
                    onClick={() => { setPage(0); fetchData(); }}
                    disabled={loading}
                  >
                    {loading ? t('trainerLibraryManager.applying') : t('trainerLibraryManager.applyFilters')}
                  </button>
                </div>
              </div>
            </div>

            {/* Creador / Edición */}
            <div className="card shadow-sm tlm-card" ref={creatorCardRef}>
              <div className={`card-header text-white fw-semibold ${editingId ? 'bg-primary' : 'bg-success'}`}>
                {editingId ? t('trainerLibraryManager.editTemplate') : t('trainerLibraryManager.newTemplate')}
              </div>
              <div className="card-body">
                <div className="mb-2">
                  <label className="form-label">{t('trainerLibraryManager.name')}</label>
                  <input
                    ref={nameRef}
                    className="form-control"
                    value={newTpl.name}
                    onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })}
                    placeholder="VO2max 5 x 1000 m"
                  />
                </div>

                <div className="row">
                  <div className="col-6 mb-2">
                    <label className="form-label">{t('trainerLibraryManager.sport')}</label>
                    <select
                      className="form-select"
                      value={newTpl.sport}
                      onChange={(e) => setNewTpl({ ...newTpl, sport: e.target.value })}
                    >
                      <option value="correr">{t('activityLabels.running')}</option>
                      <option value="ciclismo">{t('activityLabels.cycling')}</option>
                      <option value="nadar">{t('activityLabels.swimming')}</option>
                      <option value="fuerza">{t('activityLabels.strength')}</option>
                    </select>
                  </div>
                  <div className="col-6 mb-2">
                    <label className="form-label">{t('trainerLibraryManager.tag')}</label>
                    <select
                      className="form-select"
                      value={newTpl.tags}
                      onChange={(e) => setNewTpl({ ...newTpl, tags: e.target.value })}
                    >
                      <option value="base">{t('trainerLibraryManager.tagBase')}</option>
                      <option value="threshold">{t('trainerLibraryManager.tagThreshold')}</option>
                      <option value="vo2">{t('trainerLibraryManager.tagVo2')}</option>
                    </select>
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label">{t('trainerLibraryManager.intensityText')}</label>
                  <input
                    className="form-control"
                    value={newTpl.intensity}
                    onChange={(e) => setNewTpl({ ...newTpl, intensity: e.target.value })}
                    placeholder={t("trainerLibrary.intensityPlaceholder")}
                  />
                </div>

                <div className="row">
                  <div className="col-4 mb-2">
                    <label className="form-label">{t('trainerLibraryManager.durationMin')}</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newTpl.expected.durationMin}
                      onChange={(e) =>
                        setNewTpl({
                          ...newTpl,
                          expected: { ...newTpl.expected, durationMin: e.target.value }
                        })
                      }
                    />
                  </div>
                  <div className="col-4 mb-2">
                    <label className="form-label">{t('trainerLibraryManager.distanceKm')}</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newTpl.expected.distanceKm}
                      onChange={(e) =>
                        setNewTpl({
                          ...newTpl,
                          expected: { ...newTpl.expected, distanceKm: e.target.value }
                        })
                      }
                    />
                  </div>
                  <div className="col-4 mb-2">
                    <label className="form-label">{t('trainerLibraryManager.tss')}</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newTpl.expected.tss}
                      onChange={(e) =>
                        setNewTpl({
                          ...newTpl,
                          expected: { ...newTpl.expected, tss: e.target.value }
                        })
                      }
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">{t('trainerLibraryManager.description')}</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={newTpl.description}
                    onChange={(e) => setNewTpl({ ...newTpl, description: e.target.value })}
                    placeholder={t('trainerLibraryManager.descriptionPlaceholder')}
                  />
                </div>

                <div className="d-flex justify-content-end gap-2">
                  {editingId && (
                    <button className="btn btn-outline-secondary" onClick={resetCreator}>
                      {t('common.cancel')}
                    </button>
                  )}
                  <button className={editingId ? 'btn btn-primary' : 'btn btn-success'} onClick={saveTemplate}>
                    {editingId ? t('trainerLibraryManager.update') : t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Columna derecha: listado */}
          <section className="tlm-right">
            <div className="card shadow-sm tlm-card">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <div className="fw-semibold">
                  {t('trainerLibraryManager.templates')} <span className="badge bg-secondary">{total}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">
                    {Math.min(total, skip + 1)}–{Math.min(total, skip + items.length)} {t('trainerLibraryManager.of')} {total}
                  </small>
                  <div className="btn-group btn-group-sm">
                    <button
                      className="btn btn-outline-secondary"
                      disabled={page <= 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      ← {t('trainerLibraryManager.previous')}
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      disabled={page + 1 >= totalPages()}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t('trainerLibraryManager.next')} →
                    </button>
                  </div>
                </div>
              </div>

              {/* GRID de tarjetas */}
              <div className="p-3 tlm-list-grid">
                {loading && <div className="text-center py-4 w-100">{t('common.loading')}</div>}

                {!loading && items.length === 0 && (
                  <div className="text-center text-muted py-5 w-100">
                    {t('trainerLibraryManager.noTemplatesForFilters')}
                  </div>
                )}

                {!loading && items.map((it) => (
                  <div key={it._id} className="card shadow-sm tlm-item">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="tlm-title fw-semibold">{it.name}</div>
                        <div className="btn-group btn-group-sm tlm-actions">
                          <button className="btn btn-outline-primary" onClick={() => startEdit(it)}>
                            {t('trainerLibraryManager.edit')}
                          </button>
                          <button className="btn btn-outline-secondary" onClick={() => duplicate(it)}>
                            {t('trainerLibraryManager.duplicate')}
                          </button>
                          <button className="btn btn-outline-danger" onClick={() => softDelete(it._id)}>
                            {t('trainerLibraryManager.disable')}
                          </button>
                        </div>
                      </div>

                      <div className="tlm-meta">
                        <span className="badge bg-light text-dark me-1">{it.sport}</span>
                        <span className="badge bg-light text-dark me-1">{it.tags}</span>
                        <span className="text-muted">
                          · TSS {it?.expected?.tss ?? '–'} · {it?.expected?.durationMin ?? '–'} min
                          {it.updatedAt && <> · {t('trainerLibraryManager.updatedShort')} {dayjs(it.updatedAt).format('DD/MM/YYYY')}</>}
                        </span>
                      </div>

                      {it.description && <div className="tlm-desc">{it.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer de paginación (abajo) */}
            <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
              <small className="text-muted">
                {t('trainerLibraryManager.page')} {page + 1} {t('trainerLibraryManager.of')} {totalPages()}
              </small>
              <div className="btn-group btn-group-sm">
                <button
                  className="btn btn-outline-secondary"
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ← {t('trainerLibraryManager.previous')}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  disabled={page + 1 >= totalPages()}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('trainerLibraryManager.next')} →
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}